import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ITransplantedPatients } from '@nkr/models/index';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Glyphicon, Label } from 'react-bootstrap';
import { GridWrapper } from './styles';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import AlleleMatch from '@nkr/components/allele-match';

interface IProps {
    data: ITransplantedPatients[],
    isAdminOnly: boolean,
    onGridReady: (e: GridReadyEvent) => void
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);

        e.api.sizeColumnsToFit();
    }

    const onGridSizeChanged = (e: GridSizeChangedEvent) => {
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const stringBooleanCell = (value: string) => {
        if (value == 'Y') {
            return <Glyphicon glyph='ok' />
        }

        else if (value == 'N') {
            return <Glyphicon glyph='remove' />
        }

        return <Glyphicon glyph='question-sign' />
    }

    const reportColumns: ColDef[] = [
        {
            colId: 'Rank',
            field: 'rank',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            headerName: 'Rank',
            width: 35,
            sortable: false,
            suppressSizeToFit: true,
            pinnedRowCellRendererFramework: () => <div></div>,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            colId: 'transplantDate',
            field: 'transplantDate',
            headerName: 'TransplantDate',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP<br />Date</SortableHeaderComponent>,
            width: 180,
            comparator: Utility.dateComparator
        },
        {
            colId: 'recipientCenterAlias',
            field: 'recipientCenterAlias',
            headerName: 'Recipient Center',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient<br/>Center</SortableHeaderComponent>,
            width: 180,
            hide: !props.isAdminOnly
        },
        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'RecipientAlias',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Alias</SortableHeaderComponent>,
            width: 180,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedPatients;
                return <Link to={`/center/${data.recipientCenterId}/recipient/${data.recipientId}/profile/general`} target='_blank' rel='noopener noreferrer'>{data.recipientAlias}</Link>;
            },
        },
        {
            colId: 'recipientType',
            field: 'recipientType',
            headerName: 'RecipientType',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Type</SortableHeaderComponent>,
            width: 270,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedPatients;
                let style = 'info';

                if (data.recipientType == "Kidney Replacement Case") {
                    style = 'danger';
                }

                if (data.recipientType == "Paired Patient") {
                    style = 'default';
                }

                if (data.recipientType == "Waitlist Patient") {
                    style = 'warning';
                }

                if (data.recipientType == "Standard Voucher Holder" || data.recipientType == "Family Voucher Holder") {
                    style = 'success';
                }

                return <Label bsStyle={style}>{data.recipientType}</Label>;
            },
        },
        {
            colId: 'recipientAgeATOT',
            field: 'recipientAgeATOT',
            headerName: 'RecipientAgeATOT',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Age<br />At Time<br />of TXP</SortableHeaderComponent>,
            width: 140,
        },
        {
            colId: 'recipientABO',
            field: 'recipientABO',
            headerName: 'RecipientABO',
            width: 100,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>ABO</SortableHeaderComponent>,
        },
        {
            colId: 'txpDonorCenterAlias',
            field: 'txpDonorCenterAlias',
            headerName: 'TXPDonorCenter',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP<br />Donor<br />Center</SortableHeaderComponent>,
            width: 160,
        },
        {
            colId: 'txpDonorUnosID',
            field: 'txpDonorUnosID',
            headerName: 'TXPDonorUNOSID',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP<br />Donor<br />UNOS ID</SortableHeaderComponent>,
            width: 160,
        },
        {
            colId: 'txpDonorAlias',
            field: 'txpDonorAlias',
            headerName: 'TXPDonorAlias',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP<br />Donor<br />Alias</SortableHeaderComponent>,
            width: 160,
        },
        {
            colId: 'txpDonorAgeATOT',
            field: 'txpDonorAgeATOT',
            headerName: 'TXPDonorAgeATOT',
            width: 200,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP Donor Age<br />At Time<br />of Transplant</SortableHeaderComponent>,
        },
        {
            colId: 'txpDonorABO',
            field: 'txpDonorABO',
            headerName: 'TXPDonorABO',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>TXP<br />Donor<br />ABO</SortableHeaderComponent>,
            width: 140,
        },
        {
            colId: 'epletMM',
            field: 'epletMM',
            headerName: 'EpletMM',
            width: 90,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Eplet<br />MM</SortableHeaderComponent>,
        },
        {
            colId: 'bmtMM',
            field: 'bmtMM',
            headerName: 'AlleleMatch',
            width: 90,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Allele <br />Match</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedPatients;

                if (data.bmtMM == '--') {
                    return '--';
                }

                return <AlleleMatch bmtMm={parseInt(data.bmtMM)} displayAsInline={false} />;
            }
        },
        {
            colId: 'recipientCPRA',
            field: 'recipientCPRA',
            headerName: 'RecipientCpraATOT',
            width: 180,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>cPRA<br />At Time<br />of Transplant</SortableHeaderComponent>,
        },
        {
            colId: 'wasDesensitized',
            field: 'wasDesensitized',
            headerName: 'WasDesensitized',
            width: 180,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Was<br />Desensitized?</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as ITransplantedPatients;

                return stringBooleanCell(data.wasDesensitized);
            },
        },
        {
            colId: 'hasAntibodyScreeningResults',
            field: 'hasAntibodyScreeningResults',
            headerName: 'AntibodyScreeningResults',
            width: 180,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Antibody<br />Screening<br />Results</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedPatients;
                return <Link to={`/center/${data.recipientCenterId}/recipient/${data.recipientId}/profile/antibody-screening`} target='_blank' rel='noopener noreferrer'>{stringBooleanCell(data.hasAntibodyScreeningResults)}</Link>;
            },
        },
        {
            colId: 'hasDsaDetected',
            field: 'hasDsaDetected',
            headerName: 'DSADetected',
            width: 180,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>DSA<br />Detected?</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ITransplantedPatients;
                return <Link to={`/center/${data.recipientCenterId}/recipient/${data.recipientId}/profile/antibody-screening?openDetails=true`} target='_blank' rel='noopener noreferrer'>{stringBooleanCell(data.hasDsaDetected)}</Link>;
            },
        },
        {
            colId: 'rejectionEpisodes',
            field: 'rejectionEpisodes',
            headerName: 'RejectionEpisodes',
            width: 180,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Rejection<br />Episodes?
            </SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as ITransplantedPatients;

                return stringBooleanCell(data.rejectionEpisodes);
            },
        },
        {
            colId: 'graftFunctioning',
            field: 'graftFunctioning',
            headerName: 'GraftFunctioning',
            width: 180,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Graft<br />Functioning?</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as ITransplantedPatients;

                return stringBooleanCell(data.graftFunctioning);
            },
        },
        {
            colId: 'patientSurvival',
            field: 'patientSurvival',
            headerName: 'PatientSurvival',
            width: 180,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Patient<br />Survival?</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as ITransplantedPatients;

                return stringBooleanCell(data.patientSurvival);
            },
        }
    ];

    const columnDefs = useMemo(() => reportColumns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap'>
            <AgGridReact
                enableCellTextSelection={true}
                defaultColDef={{
                    headerClass: 'center-list-header',
                    cellClass: 'center-list-cell',
                    sortable: true,
                    resizable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={60}
                rowHeight={40}
                noRowsOverlayComponentFramework={() => <div>No data for the selected date range...</div>}
                suppressCellSelection={true}
                onGridReady={onGridReady}
                onGridSizeChanged={onGridSizeChanged}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
                onRowDataChanged={onRowDataChanged}
            />
        </GridWrapper>
    );
}

export default Grid;